import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { BookOpen, Loader2, CheckCircle2, XCircle, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';
import { notify } from '../lib/toast';
import type { InviteInfo } from '../lib/api';

export function InviteAcceptPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore();

  const ROLE_LABELS = {
    editor: t('inviteAccept.roleEditor', 'Editor'),
    commenter: t('inviteAccept.roleCommenter', 'Comentarista'),
    viewer: t('inviteAccept.roleViewer', 'Visualizador'),
  };

  const ROLE_DESCRIPTIONS = {
    editor: t('inviteAccept.roleDescriptionEditor', 'Você poderá ler e editar capítulos do projeto.'),
    commenter: t('inviteAccept.roleDescriptionCommenter', 'Você poderá ler e comentar nos capítulos do projeto.'),
    viewer: t('inviteAccept.roleDescriptionViewer', 'Você poderá visualizar o projeto em modo somente leitura.'),
  };

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  // Check auth state on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Load invite info
  useEffect(() => {
    if (!token) {
      setError(t('inviteAccept.invalidToken', 'Token de convite inválido.'));
      setIsLoadingInvite(false);
      return;
    }

    api
      .getInvite(token)
      .then((res) => {
        setInvite(res.invite);
        if (res.invite.status === 'declined') {
          setError(t('inviteAccept.inviteDeclined', 'Este convite foi recusado.'));
        } else if (res.invite.status === 'active') {
          // Already accepted — redirect immediately
          setAccepted(true);
        }
      })
      .catch(() => {
        setError(t('inviteAccept.inviteNotFound', 'Convite não encontrado ou expirado.'));
      })
      .finally(() => {
        setIsLoadingInvite(false);
      });
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;

    // Not authenticated — redirect to login with return URL
    if (!isAuthenticated) {
      sessionStorage.setItem('inviteToken', token);
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }

    setIsAccepting(true);
    try {
      const res = await api.acceptInvite(token);
      setAccepted(true);
      notify.success(t('inviteAccept.inviteAcceptedToast', 'Convite aceito! Você agora tem acesso ao projeto.'));
      // Navigate to the project editor
      setTimeout(() => {
        navigate(`/app/editor/${res.projectId}`);
      }, 1500);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || t('inviteAccept.acceptError', 'Erro ao aceitar convite');
      notify.error(msg);
    } finally {
      setIsAccepting(false);
    }
  };

  // Redirect to editor if already accepted
  useEffect(() => {
    if (accepted && invite && isAuthenticated && !isAccepting) {
      const timer = setTimeout(() => {
        navigate(`/app/editor/${invite.projectId}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [accepted, invite, isAuthenticated, isAccepting, navigate]);

  const isPageLoading = isLoadingInvite || authLoading;

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            <BookOpen className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-serif font-bold text-xl text-foreground">Articulat</span>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 shadow-xl shadow-black/5">
          {/* Loading state */}
          {isPageLoading && (
            <div className="flex flex-col items-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t('inviteAccept.loadingInvite', 'Carregando convite...')}</p>
            </div>
          )}

          {/* Error state */}
          {!isPageLoading && error && (
            <div className="flex flex-col items-center py-8 gap-4 text-center">
              <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-7 w-7 text-destructive" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground text-lg">{t('inviteAccept.invalidInviteTitle', 'Convite inválido')}</h2>
                <p className="text-sm text-muted-foreground mt-1">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                {t('inviteAccept.backToHome', 'Voltar ao início')}
              </Button>
            </div>
          )}

          {/* Accepted state */}
          {!isPageLoading && !error && accepted && (
            <div className="flex flex-col items-center py-8 gap-4 text-center">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center"
              >
                <CheckCircle2 className="h-7 w-7 text-green-500" />
              </motion.div>
              <div>
                <h2 className="font-semibold text-foreground text-lg">{t('inviteAccept.inviteAcceptedTitle', 'Convite aceito!')}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('inviteAccept.redirectingToProject', 'Redirecionando para o projeto...')}
                </p>
              </div>
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Invite info + accept button */}
          {!isPageLoading && !error && !accepted && invite && (
            <div className="space-y-5">
              {/* Header */}
              <div className="text-center space-y-1.5">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <UserPlus className="h-6 w-6 text-primary" />
                </div>
                <h2 className="font-serif font-semibold text-foreground text-xl">
                  {t('inviteAccept.collaborationInviteTitle', 'Convite de colaboração')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{invite.inviterName}</span> {t('inviteAccept.invitedYouToCollaborate', 'convidou você para colaborar')}
                </p>
              </div>

              {/* Project card */}
              <div className="rounded-xl bg-muted/40 border border-border p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{invite.projectTitle}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('inviteAccept.yourRole', 'Seu papel:')}{' '}
                      <span className="font-medium text-foreground">
                        {ROLE_LABELS[invite.role]}
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground pl-13">
                  {ROLE_DESCRIPTIONS[invite.role]}
                </p>
              </div>

              {/* Email notice */}
              <p className="text-xs text-center text-muted-foreground">
                {t('inviteAccept.thisInviteIsFor', 'Este convite é para')}{' '}
                <span className="font-medium text-foreground">{invite.email}</span>.
                {!isAuthenticated &&
                  ' ' + t('inviteAccept.needToSignInToAccept', 'Você precisará entrar com esta conta para aceitar.')}
              </p>

              {/* Accept button */}
              <Button
                className="w-full"
                size="lg"
                onClick={handleAccept}
                disabled={isAccepting}
              >
                {isAccepting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {isAuthenticated ? t('inviteAccept.acceptInvite', 'Aceitar convite') : t('inviteAccept.signInToAccept', 'Entrar para aceitar')}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={() => navigate('/')}
              >
                {t('inviteAccept.decline', 'Recusar')}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
